import { Box, Button, Chip, Typography } from "@mui/material";
import { useMemo, useState } from "react";

export type InteractiveChoiceOption = {
  value: string | number;
  label: string;
  // When set, the option belongs to a collapsible group (the parent niche
  // name). The option whose `label === group` is the group's "select all"
  // toggle; the rest are its children.
  group?: string;
};

type Props = {
  question: string;
  options: InteractiveChoiceOption[];
  multiSelect: boolean;
  minSelections: number;
  maxSelections: number | null;
  disabled?: boolean;
  hideQuestion?: boolean;
  initialSelected?: InteractiveChoiceOption[];
  onConfirm: (selected: InteractiveChoiceOption[]) => void;
};

type OptionGroup = {
  name: string;
  parentIndex: number; // index of the "select all" option, or -1
  childIndices: number[];
};

function optionKey(option: InteractiveChoiceOption): string {
  return `${option.value}::${option.label}`;
}

const InteractiveChoicePicker = ({
  question,
  options,
  multiSelect,
  minSelections,
  maxSelections,
  disabled,
  hideQuestion,
  initialSelected,
  onConfirm,
}: Props) => {
  // Key by index, not value — two options can share a value (e.g. two date chips).
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(() => {
    if (!initialSelected?.length) return new Set();
    const matched = new Set<number>();
    const seenKeys = new Set<string>();
    for (const picked of initialSelected) {
      const pickedKey = optionKey(picked);
      if (seenKeys.has(pickedKey)) continue;
      seenKeys.add(pickedKey);
      const idx = options.findIndex(
        (o, i) => !matched.has(i) && optionKey(o) === pickedKey,
      );
      if (idx >= 0) matched.add(idx);
    }
    return matched;
  });

  // Grouped niche picker when any option carries a `group`. Groups preserve
  // first-seen order; within a group the option whose label matches the group
  // name is the "All <niche>" toggle and the rest are subcategory chips.
  const isGrouped = useMemo(() => options.some((o) => o.group), [options]);
  const groups = useMemo<OptionGroup[]>(() => {
    if (!isGrouped) return [];
    const order: string[] = [];
    const byName: Record<string, OptionGroup> = {};
    options.forEach((option, index) => {
      const name = option.group ?? option.label;
      if (!byName[name]) {
        byName[name] = { name, parentIndex: -1, childIndices: [] };
        order.push(name);
      }
      if (option.label === name) byName[name].parentIndex = index;
      else byName[name].childIndices.push(index);
    });
    return order.map((name) => byName[name]);
  }, [options, isGrouped]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const selected = useMemo(
    () => options.filter((_, i) => selectedIndices.has(i)),
    [options, selectedIndices],
  );

  const max = maxSelections ?? options.length;
  const min = Math.max(1, minSelections);
  const canConfirm =
    !disabled && selected.length >= min && selected.length <= max;

  const toggle = (index: number) => {
    if (disabled) return;
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
        return next;
      }
      if (!multiSelect) {
        return new Set([index]);
      }
      if (next.size >= max) return prev;
      next.add(index);
      return next;
    });
  };

  // Selecting "All <niche>" clears that group's child picks (one whole-niche
  // pick); selecting a child clears the group's "All". Mirrors Discover.
  const toggleParent = (group: OptionGroup) => {
    if (disabled || group.parentIndex < 0) return;
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(group.parentIndex)) {
        next.delete(group.parentIndex);
        return next;
      }
      group.childIndices.forEach((i) => next.delete(i));
      if (next.size >= max) return prev;
      next.add(group.parentIndex);
      return next;
    });
  };

  const toggleChild = (group: OptionGroup, index: number) => {
    if (disabled) return;
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
        return next;
      }
      if (group.parentIndex >= 0) next.delete(group.parentIndex);
      if (next.size >= max) return prev;
      next.add(index);
      return next;
    });
  };

  const toggleExpanded = (name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(selected);
  };

  const helper = multiSelect
    ? `Pick ${
        min === max ? min : maxSelections == null ? `${min}+` : `${min}–${max}`
      }`
    : "Pick one";

  // Answered state: just show what was picked, flat — grouping no longer matters.
  if (disabled) {
    if (selected.length === 0) return null;
    return (
      <Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {selected.map((option, i) => (
          <Chip
            key={`${i}-${optionKey(option)}`}
            label={option.label}
            variant="filled"
            size="small"
            sx={{
              fontSize: 12,
              backgroundColor: "rgba(1,67,57,0.08)",
              color: "#014339",
            }}
          />
        ))}
      </Box>
    );
  }

  const footer = (
    <Box
      sx={{
        mt: 0.75,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Typography sx={{ fontSize: 11, color: "#4A5565" }}>
        {`${helper}${selected.length > 0 ? ` · ${selected.length} selected` : ""}`}
      </Typography>
      <Button
        size="small"
        variant="contained"
        disabled={!canConfirm}
        onClick={handleConfirm}
        sx={{ textTransform: "none", fontSize: 12, py: 0.25 }}
      >
        Confirm
      </Button>
    </Box>
  );

  if (isGrouped) {
    return (
      <Box sx={{ mt: 0.75 }}>
        {question && !hideQuestion && (
          <Typography
            sx={{ fontSize: 13, fontWeight: 600, color: "#0F2F2A", mb: 0.75 }}
          >
            {question}
          </Typography>
        )}
        <Box
          sx={{
            border: "1px solid rgba(1,67,57,0.1)",
            borderRadius: 2,
            overflow: "hidden",
            maxHeight: 440,
            overflowY: "auto",
            backgroundColor: "#fff",
          }}
        >
          {groups.map((group, groupIndex) => {
            const parentSelected =
              group.parentIndex >= 0 && selectedIndices.has(group.parentIndex);
            const isOpen = expanded.has(group.name);
            const childSelectedCount = group.childIndices.filter((i) =>
              selectedIndices.has(i),
            ).length;
            const hasSelection = parentSelected || childSelectedCount > 0;
            const selectionSummary = parentSelected
              ? "All"
              : group.childIndices
                  .filter((i) => selectedIndices.has(i))
                  .map((i) => options[i].label)
                  .join(", ");
            return (
              <Box
                key={group.name}
                sx={{
                  borderBottom:
                    groupIndex < groups.length - 1
                      ? "1px solid rgba(1,67,57,0.06)"
                      : "none",
                }}
              >
                <Box
                  onClick={() => toggleExpanded(group.name)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.25,
                    py: 1,
                    cursor: "pointer",
                    borderLeft: hasSelection
                      ? "3px solid #014339"
                      : "3px solid transparent",
                    backgroundColor: hasSelection
                      ? "rgba(1,67,57,0.08)"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: hasSelection
                        ? "rgba(1,67,57,0.12)"
                        : "rgba(1,67,57,0.04)",
                    },
                  }}
                >
                  {hasSelection ? (
                    <Box
                      component="i"
                      className="fa-solid fa-circle-check"
                      sx={{ fontSize: 13, color: "#014339", width: 14 }}
                    />
                  ) : (
                    <Box sx={{ width: 14 }} />
                  )}
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: hasSelection ? 600 : 500,
                      color: hasSelection ? "#014339" : "#0F2F2A",
                    }}
                  >
                    {group.name}
                  </Typography>
                  {hasSelection && (
                    <Typography
                      sx={{
                        ml: "auto",
                        maxWidth: 130,
                        fontSize: 11,
                        fontWeight: 500,
                        color: "#014339",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {selectionSummary}
                    </Typography>
                  )}
                  <Box
                    component="i"
                    className={`fa-regular fa-chevron-${isOpen ? "down" : "right"}`}
                    sx={{
                      ml: hasSelection ? 0.75 : "auto",
                      fontSize: 11,
                      color: "#4A5565",
                      width: 12,
                      textAlign: "center",
                    }}
                  />
                </Box>
                {isOpen && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.75,
                      px: 1.25,
                      pb: 1,
                      pt: 0.25,
                      backgroundColor: "#fafaf6",
                    }}
                  >
                    <Chip
                      label={`All ${group.name}`}
                      clickable
                      onClick={() => toggleParent(group)}
                      variant={parentSelected ? "filled" : "outlined"}
                      color={parentSelected ? "primary" : "default"}
                      size="small"
                      sx={{ fontSize: 12, fontWeight: 600 }}
                    />
                    {group.childIndices.map((index) => {
                      const childSelected =
                        parentSelected || selectedIndices.has(index);
                      return (
                        <Chip
                          key={`${index}-${optionKey(options[index])}`}
                          label={options[index].label}
                          clickable={!parentSelected}
                          onClick={() =>
                            parentSelected
                              ? undefined
                              : toggleChild(group, index)
                          }
                          variant={childSelected ? "filled" : "outlined"}
                          color={childSelected ? "primary" : "default"}
                          size="small"
                          disabled={parentSelected}
                          sx={{ fontSize: 12 }}
                        />
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
        {footer}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 0.75 }}>
      {question && !hideQuestion && (
        <Typography
          sx={{ fontSize: 13, fontWeight: 600, color: "#0F2F2A", mb: 0.75 }}
        >
          {question}
        </Typography>
      )}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {options.map((option, index) => {
          const isSelected = selectedIndices.has(index);
          return (
            <Chip
              key={`${index}-${optionKey(option)}`}
              label={option.label}
              clickable
              onClick={() => toggle(index)}
              variant={isSelected ? "filled" : "outlined"}
              color={isSelected ? "primary" : "default"}
              size="small"
              sx={{ fontSize: 12 }}
            />
          );
        })}
      </Box>
      {footer}
    </Box>
  );
};

export default InteractiveChoicePicker;
